import React, { useEffect, useMemo, useState } from "react";
import useMediaQuery from "@mui/material/useMediaQuery";
import API from "../../api/api";

function q2(n) {
  const x = Number(n || 0);
  if (!Number.isFinite(x)) return 0;
  // Match backend style: 2 decimals
  return Math.floor(x * 100) / 100;
}

function TextInput({ label, value, onChange, placeholder, type = "text", style }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <label style={{ fontSize: 12, color: "#64748b" }}>{label}</label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        type={type}
        style={{
          padding: "10px 12px",
          borderRadius: 8,
          border: "1px solid #e2e8f0",
          outline: "none",
          background: "#fff",
          ...style,
        }}
      />
    </div>
  );
}

function Select({ label, value, onChange, options, style }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <label style={{ fontSize: 12, color: "#64748b" }}>{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          padding: "10px 12px",
          borderRadius: 8,
          border: "1px solid #e2e8f0",
          outline: "none",
          background: "#fff",
          ...style,
        }}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function Badge({ children, color = "#1f2937", bg = "#e5e7eb" }) {
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 8px",
        fontSize: 12,
        borderRadius: 999,
        color,
        background: bg,
        fontWeight: 600,
      }}
    >
      {children}
    </span>
  );
}

function PrimaryButton({ children, disabled, onClick, style }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: "10px 12px",
        background: "#0f172a",
        color: "#fff",
        border: 0,
        borderRadius: 8,
        cursor: disabled ? "not-allowed" : "pointer",
        width: "100%",
        ...style,
      }}
    >
      {children}
    </button>
  );
}

function SecondaryButton({ children, disabled, onClick, style }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: "10px 12px",
        background: "#fff",
        color: "#0f172a",
        border: "1px solid #e2e8f0",
        borderRadius: 8,
        cursor: disabled ? "not-allowed" : "pointer",
        width: "100%",
        ...style,
      }}
    >
      {children}
    </button>
  );
}

function ActionButton({ children, onClick, disabled, variant = "neutral" }) {
  const stylesByVariant = {
    approve: { background: "#059669", color: "#fff" },
    reject: { background: "#ef4444", color: "#fff" },
    neutral: { background: "#0f172a", color: "#fff" },
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: "10px 12px",
        border: 0,
        borderRadius: 8,
        cursor: disabled ? "not-allowed" : "pointer",
        fontWeight: 600,
        ...stylesByVariant[variant],
      }}
    >
      {children}
    </button>
  );
}

function parseTrieduClaimNote(note, fallbackFeePercent = 0) {
  if (!note) return null;
  const isClaim = Boolean(
    note.includes("trieducation") ||
    note.includes("Coin Claim") ||
    note.includes("Net Disbursed") ||
    note.includes("Direct Bank Fee")
  );
  if (!isClaim) return null;

  const grossMatch = note.match(/Gross ₹([\d.]+)/i);
  const feeMatch = note.match(/Direct Bank Fee \(([\d.]+)%\):\s*₹([\d.]+)/i);
  const netMatch = note.match(/Net Disbursed:\s*₹([\d.]+)/i);

  const gross = grossMatch ? Number(grossMatch[1]) : null;
  const feePercent = feeMatch ? Number(feeMatch[1]) : Number(fallbackFeePercent || 0);
  const feeAmount = feeMatch ? Number(feeMatch[2]) : 0;
  const netDisbursed = netMatch ? Number(netMatch[1]) : null;

  return {
    isClaim: true,
    gross,
    feePercent,
    feeAmount,
    netDisbursed,
  };
}

export default function AdminWithdrawals() {
  // Breakpoints
  // Mobile: <600px, Tablet: 600-1024px, Desktop: >1024px
  const isMobile = useMediaQuery("(max-width:599.95px)");
  const isDesktop = useMediaQuery("(min-width:1024.05px)");

  const [filters, setFilters] = useState({
    status: "pending",
    user: "",
    date_from: "",
    date_to: "",
    min_amount: "",
    max_amount: "",
    method: "",
    ordering: "-requested_at",
  });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [rows, setRows] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);

  // Full Unified Withdrawal Configuration State
  const [limitsCfg, setLimitsCfg] = useState(() => {
    try {
      const raw = localStorage.getItem("tri_withdrawal_limits");
      if (raw) return JSON.parse(raw);
    } catch (_) {}
    return { min_withdrawal: 500, max_withdrawal: 25000, daily_max_withdrawal: 50000 };
  });

  const weekdayOptions = useMemo(
    () => [
      { value: -1, label: "All Days (24x7 Open)" },
      { value: 0, label: "Monday" },
      { value: 1, label: "Tuesday" },
      { value: 2, label: "Wednesday (Default)" },
      { value: 3, label: "Thursday" },
      { value: 4, label: "Friday" },
      { value: 5, label: "Saturday" },
      { value: 6, label: "Sunday" },
    ],
    []
  );

  const [windowCfg, setWindowCfg] = useState(() => {
    try {
      const raw = localStorage.getItem("tk_withdrawals_window");
      if (raw) return JSON.parse(raw);
    } catch (_) {}
    return { enabled: true, weekday: 2, start_time: "00:00", end_time: "23:59" };
  });

  const [taxPercent, setTaxPercent] = useState(10.0);
  const [sponsorPercent, setSponsorPercent] = useState(2.5);
  const [directBankFeePercent, setDirectBankFeePercent] = useState(3.0);

  const [windowSaving, setWindowSaving] = useState(false);
  const [windowErr, setWindowErr] = useState("");
  const [windowOk, setWindowOk] = useState("");
  const [showConfig, setShowConfig] = useState(true);

  function setF(key, val) {
    setFilters((f) => ({ ...f, [key]: val }));
  }

  const methodOptions = useMemo(
    () => [
      { value: "", label: "Any method" },
      { value: "upi", label: "UPI" },
      { value: "bank", label: "Bank Transfer" },
    ],
    []
  );
  const statusOptions = useMemo(
    () => [
      { value: "", label: "Any status" },
      { value: "pending", label: "Pending" },
      { value: "approved", label: "Approved" },
      { value: "rejected", label: "Rejected" },
    ],
    []
  );

  async function fetchWithdrawals() {
    setLoading(true);
    setErr("");
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== null && v !== undefined && String(v).trim() !== "") {
          params[k] = v;
        }
      });
      const res = await API.get("/admin/withdrawals/", { params });
      const items = res?.data?.results || res?.data || [];
      setRows(Array.isArray(items) ? items : []);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load withdrawals");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  async function fetchWithdrawTax() {
    try {
      const res = await API.get("/admin/commission/master/", { cacheTTL: 10_000, dedupe: "cancelPrevious" });
      const d = res?.data || {};
      
      const pct = Number(
        d?.taxes_and_charges?.withdrawal_tax_percent ??
        d?.withdrawal?.tax_percent ??
        d?.tax?.withdrawal_tax_percent ??
        10
      );
      setTaxPercent(Number.isFinite(pct) ? pct : 10);

      const sp = Number(d?.withdrawal?.sponsor_percent ?? 0);
      setSponsorPercent(Number.isFinite(sp) ? sp : 0);

      const dbf = Number(d?.withdrawal?.direct_bank_fee_percent ?? 3.0);
      setDirectBankFeePercent(Number.isFinite(dbf) ? dbf : 3.0);

      if (d?.withdrawals_window) {
        const w = d.withdrawals_window;
        let dayVal = Number(w?.weekday ?? 2);
        if (dayVal === 7 || dayVal === -1) dayVal = -1;
        const wObj = {
          enabled: Boolean(w?.enabled ?? true),
          weekday: dayVal,
          start_time: String(w?.start_time ?? "00:00").slice(0, 5),
          end_time: String(w?.end_time ?? "23:59").slice(0, 5),
        };
        setWindowCfg(wObj);
        try { localStorage.setItem("tk_withdrawals_window", JSON.stringify(wObj)); } catch (_) {}
      }

      if (d?.withdrawal_limits) {
        const l = d.withdrawal_limits;
        const lObj = {
          min_withdrawal: Number(l?.min_withdrawal ?? 500),
          max_withdrawal: Number(l?.max_withdrawal ?? 25000),
          daily_max_withdrawal: Number(l?.daily_max_withdrawal ?? 50000),
        };
        setLimitsCfg(lObj);
        try { localStorage.setItem("tri_withdrawal_limits", JSON.stringify(lObj)); } catch (_) {}
      }
    } catch (_) {}
  }

  async function saveAllWithdrawalSettings() {
    setWindowSaving(true);
    setWindowErr("");
    setWindowOk("");
    try {
      const payload = {
        withdrawal_limits: {
          min_withdrawal: Number(limitsCfg.min_withdrawal || 500),
          max_withdrawal: Number(limitsCfg.max_withdrawal || 25000),
          daily_max_withdrawal: Number(limitsCfg.daily_max_withdrawal || 50000),
        },
        withdrawals_window: {
          enabled: Boolean(windowCfg.enabled),
          weekday: Number(windowCfg.weekday),
          start_time: String(windowCfg.start_time || "00:00").slice(0, 5),
          end_time: String(windowCfg.end_time || "23:59").slice(0, 5),
        },
        tax: { percent: Number(taxPercent || 10) },
        withdrawal: {
          sponsor_percent: Number(sponsorPercent || 0),
          direct_bank_fee_percent: Number(directBankFeePercent || 0),
        },
      };

      const res = await API.patch("/admin/commission/master/", payload);
      const d = res?.data || {};
      if (d?.withdrawal_limits) {
        setLimitsCfg(d.withdrawal_limits);
      }
      if (d?.withdrawals_window) {
        let dayVal = Number(d.withdrawals_window?.weekday ?? 2);
        if (dayVal === 7 || dayVal === -1) dayVal = -1;
        setWindowCfg({
          enabled: Boolean(d.withdrawals_window?.enabled ?? true),
          weekday: dayVal,
          start_time: String(d.withdrawals_window?.start_time ?? "00:00").slice(0, 5),
          end_time: String(d.withdrawals_window?.end_time ?? "23:59").slice(0, 5),
        });
      }

      try {
        localStorage.setItem("tri_withdrawal_limits", JSON.stringify(payload.withdrawal_limits));
        localStorage.setItem("tk_withdrawals_window", JSON.stringify(payload.withdrawals_window));
      } catch (_) {}

      setWindowOk("All withdrawal settings & limits saved successfully!");
      setTimeout(() => setWindowOk(""), 3500);
    } catch (e) {
      setWindowErr(e?.response?.data?.detail || "Failed to save withdrawal configuration");
    } finally {
      setWindowSaving(false);
    }
  }

  useEffect(() => {
    fetchWithdrawTax();
    fetchWithdrawals();
  }, []);

  async function handleApprove(row) {
    const payout_ref = window.prompt("Enter payout reference (optional):", row.payout_ref || "");
    if (payout_ref === null) return;
    try {
      await API.patch(`/admin/withdrawals/${row.id}/approve/`, { payout_ref });
      await fetchWithdrawals();
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to approve");
    }
  }

  async function handleReject(row) {
    const reason = window.prompt("Enter reject reason (optional):", "");
    if (reason === null) return;
    try {
      await API.patch(`/admin/withdrawals/${row.id}/reject/`, { reason });
      await fetchWithdrawals();
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to reject");
    }
  }

  // Bulk Approve Selected
  async function handleBulkApprove() {
    if (!selectedIds.length) return;
    const ok = window.confirm(`Approve all ${selectedIds.length} selected withdrawals?`);
    if (!ok) return;
    setBulkLoading(true);
    try {
      for (const id of selectedIds) {
        await API.patch(`/admin/withdrawals/${id}/approve/`, { payout_ref: "BULK_DISBURSED" });
      }
      setSelectedIds([]);
      await fetchWithdrawals();
      alert(`Successfully approved ${selectedIds.length} withdrawals.`);
    } catch (e) {
      alert(e?.response?.data?.detail || "Bulk approval completed with some errors.");
    } finally {
      setBulkLoading(false);
    }
  }

  // Export Bank Batch CSV (Standard HDFC / ICICI / SBI format)
  function handleExportBankBatch() {
    const targetRows = selectedIds.length
      ? rows.filter((r) => selectedIds.includes(r.id))
      : rows.filter((r) => r.status === "pending");

    if (!targetRows.length) {
      alert("No withdrawals available to export.");
      return;
    }

    const headers = [
      "Payment Type",
      "Beneficiary Name",
      "Beneficiary Account No",
      "IFSC Code",
      "Net Amount (INR)",
      "Remarks / User ID",
      "Digital Rank",
      "Purchased Packages",
      "Debit Account No",
    ];

    const csvRows = targetRows.map((r) => {
      const isTrieduClaim = Boolean(
        r.note && (r.note.includes("trieducation") || r.note.includes("Coin Claim") || r.note.includes("Net Disbursed") || r.note.includes("Direct Bank Fee"))
      );
      const gross = Number(r.amount || 0);
      const taxAmt = isTrieduClaim ? 0 : gross * Number(taxLabel || 0) / 100;
      const net = isTrieduClaim ? gross.toFixed(2) : (gross - taxAmt).toFixed(2);
      const rankStr = r.rank_label || r.rank_name || (r.rank_level ? `Layer ${r.rank_level}` : "Free Tier (L0)");
      const pkgsStr = Array.isArray(r.package_badges) && r.package_badges.length > 0 ? r.package_badges.join(" | ") : "None";
      return [
        "NEFT",
        `"${(r.full_name || r.username || "User").replace(/"/g, '""')}"`,
        `"${r.bank_account_number || r.account_number || ""}"`,
        r.ifsc_code || r.ifsc || "",
        net,
        `"TR-${r.username || r.id}"`,
        `"${rankStr.replace(/"/g, '""')}"`,
        `"${pkgsStr.replace(/"/g, '""')}"`,
        "COMPANY_MAIN_ACCOUNT",
      ];
    });

    const csvContent = "data:text/csv;charset=utf-8," + [headers.join(","), ...csvRows.map((e) => e.join(","))].join("\n");
    const link = document.createElement("a");
    link.setAttribute("href", encodeURI(csvContent));
    link.setAttribute("download", `bank_batch_payout_${new Date().toISOString().split("T")[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  const summary = useMemo(() => {
    const pending = rows.filter((r) => r.status === "pending");
    const totalPending = pending.reduce((s, r) => s + Number(r.amount || 0), 0);
    return { pendingCount: pending.length, pendingAmount: totalPending };
  }, [rows]);

  const taxLabel = useMemo(() => {
    const pct = Number(taxPercent || 0);
    return Number.isFinite(pct) ? pct : 0;
  }, [taxPercent]);

  const formatDateTime = (value) => {
    if (!value) return "";
    try {
      return new Date(value).toLocaleString();
    } catch {
      return String(value);
    }
  };

  const getStatusBadge = (r) =>
    r.status === "pending" ? (
      <Badge color="#b45309" bg="#ffedd5">
        Pending
      </Badge>
    ) : r.status === "approved" ? (
      <Badge color="#065f46" bg="#d1fae5">
        Approved
      </Badge>
    ) : (
      <Badge color="#991b1b" bg="#fee2e2">
        Rejected
      </Badge>
    );

  const resetFilters = () =>
    setFilters({
      status: "pending",
      user: "",
      date_from: "",
      date_to: "",
      min_amount: "",
      max_amount: "",
      method: "",
      ordering: "-requested_at",
    });

  return (
    <div style={{ width: "100%", maxWidth: 1280, margin: "0 auto", padding: 16 }}>
      {/* Header */}
      <div style={{ marginBottom: 16 }}>
        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: isMobile ? "flex-start" : "center",
            justifyContent: "space-between",
            gap: 8,
          }}
        >
          <div>
            <h2 style={{ margin: 0, color: "#0f172a" }}>Withdrawals</h2>
            <div style={{ color: "#64748b", fontSize: 13 }}>
              Approve or reject pending withdrawal requests. Filters help narrow results.
            </div>
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              gap: 8,
              width: isMobile ? "100%" : "auto",
            }}
          >
            {isMobile ? (
              <SecondaryButton
                disabled={loading}
                onClick={() => setShowFilters((s) => !s)}
                style={{ width: "100%" }}
              >
                {showFilters ? "Hide Filters" : "Show Filters"}
              </SecondaryButton>
            ) : null}

            <div
              style={{
                color: "#334155",
                fontSize: 14,
                padding: "10px 12px",
                border: "1px solid #e2e8f0",
                borderRadius: 8,
                background: "#fff",
                width: isMobile ? "100%" : "auto",
              }}
            >
              Pending: <b>{summary.pendingCount}</b> • Amount: <b>₹{summary.pendingAmount.toFixed(2)}</b>
            </div>

          <div
            style={{
              color: "#334155",
              fontSize: 14,
              padding: "10px 12px",
              border: "1px solid #e2e8f0",
              borderRadius: 8,
              background: "#fff",
              width: isMobile ? "100%" : "auto",
            }}
          >
            Withdrawal Tax: <b>{Number(taxLabel).toFixed(2)}%</b>
          </div>

          <button
            type="button"
            onClick={handleExportBankBatch}
            disabled={loading || rows.length === 0}
            style={{
              padding: "10px 14px",
              background: "#0284c7",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              fontWeight: 700,
              fontSize: 13,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            📥 Bank Batch CSV {selectedIds.length ? `(${selectedIds.length})` : ""}
          </button>

          {selectedIds.length > 0 && (
            <button
              type="button"
              onClick={handleBulkApprove}
              disabled={bulkLoading}
              style={{
                padding: "10px 14px",
                background: "#16a34a",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                fontWeight: 800,
                fontSize: 13,
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              {bulkLoading ? "Disbursing..." : `⚡ Bulk Approve (${selectedIds.length})`}
            </button>
          )}
          </div>
        </div>
      </div>

      {/* Unified Withdrawal Control Center & Rules */}
      <div
        style={{
          border: "1px solid #e2e8f0",
          borderRadius: 12,
          padding: 16,
          background: "#fff",
          marginBottom: 16,
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap", marginBottom: showConfig ? 14 : 0 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 18 }}>⚙️</span>
              <div style={{ fontWeight: 900, fontSize: 16, color: "#0f172a" }}>Withdrawal Configuration & Control Center</div>
              <Badge color="#0369a1" bg="#e0f2fe">Unified Admin Rules</Badge>
            </div>
            <div style={{ color: "#64748b", fontSize: 13, marginTop: 2 }}>
              Set withdrawal transaction limits, weekly payout days, operational time window (IST), and TDS / sponsor rates.
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {windowOk ? <Badge color="#065f46" bg="#d1fae5">{windowOk}</Badge> : null}
            {windowErr ? <Badge color="#991b1b" bg="#fee2e2">{windowErr}</Badge> : null}
            <SecondaryButton onClick={() => setShowConfig((s) => !s)} style={{ padding: "6px 12px", fontSize: 12, width: "auto" }}>
              {showConfig ? "Collapse Settings ▲" : "Configure Rules ▼"}
            </SecondaryButton>
          </div>
        </div>

        {showConfig && (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {/* 1. Limits & Thresholds */}
            <div style={{ background: "#f8fafc", padding: 12, borderRadius: 8, border: "1px solid #e2e8f0" }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: "#334155", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8 }}>
                1. Withdrawal Amount Limits & Caps
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isDesktop ? "repeat(3, minmax(0, 1fr))" : "repeat(1, minmax(0, 1fr))",
                  gap: 12,
                }}
              >
                <TextInput
                  label="Minimum Withdrawal Limit (₹)"
                  type="number"
                  value={limitsCfg.min_withdrawal}
                  onChange={(v) => setLimitsCfg((s) => ({ ...s, min_withdrawal: Number(v) || 0 }))}
                  placeholder="500"
                />
                <TextInput
                  label="Maximum per Request Limit (₹)"
                  type="number"
                  value={limitsCfg.max_withdrawal}
                  onChange={(v) => setLimitsCfg((s) => ({ ...s, max_withdrawal: Number(v) || 0 }))}
                  placeholder="25000"
                />
                <TextInput
                  label="Daily Maximum Cap per User (₹)"
                  type="number"
                  value={limitsCfg.daily_max_withdrawal}
                  onChange={(v) => setLimitsCfg((s) => ({ ...s, daily_max_withdrawal: Number(v) || 0 }))}
                  placeholder="50000"
                />
              </div>
            </div>

            {/* 2. Operational Window & Timing */}
            <div style={{ background: "#f8fafc", padding: 12, borderRadius: 8, border: "1px solid #e2e8f0" }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: "#334155", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8 }}>
                2. Operational Window & Weekly Day Controls
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isDesktop ? "repeat(4, minmax(0, 1fr))" : "repeat(2, minmax(0, 1fr))",
                  gap: 12,
                }}
              >
                <Select
                  label="Global Master Status"
                  value={windowCfg.enabled ? "yes" : "no"}
                  onChange={(v) => setWindowCfg((s) => ({ ...s, enabled: v === "yes" }))}
                  options={[
                    { value: "yes", label: "✅ Withdrawals Enabled" },
                    { value: "no", label: "⛔ Withdrawals Disabled" },
                  ]}
                />
                <Select
                  label="Allowed Withdrawal Day"
                  value={String(windowCfg.weekday)}
                  onChange={(v) => setWindowCfg((s) => ({ ...s, weekday: Number(v) }))}
                  options={weekdayOptions.map((o) => ({ value: String(o.value), label: o.label }))}
                />
                <TextInput
                  label="Start Time (IST)"
                  type="time"
                  value={windowCfg.start_time}
                  onChange={(v) => setWindowCfg((s) => ({ ...s, start_time: v }))}
                />
                <TextInput
                  label="End Time (IST)"
                  type="time"
                  value={windowCfg.end_time}
                  onChange={(v) => setWindowCfg((s) => ({ ...s, end_time: v }))}
                />
              </div>
            </div>

            {/* 3. Tax / TDS & Sponsor Rates */}
            <div style={{ background: "#f8fafc", padding: 12, borderRadius: 8, border: "1px solid #e2e8f0" }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: "#334155", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8 }}>
                3. Tax (TDS) & Instant / Sponsor Fee Rates
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isDesktop ? "repeat(3, minmax(0, 1fr))" : "repeat(1, minmax(0, 1fr))",
                  gap: 12,
                }}
              >
                <TextInput
                  label="Withdrawal Tax / TDS (%)"
                  type="number"
                  value={taxPercent}
                  onChange={(v) => setTaxPercent(Number(v) || 0)}
                  placeholder="10.00"
                />
                <TextInput
                  label="Instant / Sponsor Withdrawal Fee (%)"
                  type="number"
                  value={sponsorPercent}
                  onChange={(v) => setSponsorPercent(Number(v) || 0)}
                  placeholder="2.50"
                />
                <TextInput
                  label="Tri Academy Direct Bank Charges (%)"
                  type="number"
                  value={directBankFeePercent}
                  onChange={(v) => setDirectBankFeePercent(Number(v) || 0)}
                  placeholder="3.00"
                />
              </div>
            </div>

            {/* Summary & Save Action */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, pt: 4 }}>
              <div style={{ fontSize: 12, color: "#64748b" }}>
                💡 Active policy: Requests allowed between ₹{limitsCfg.min_withdrawal} and ₹{limitsCfg.max_withdrawal} (Daily cap ₹{limitsCfg.daily_max_withdrawal}) with {taxPercent}% TDS.
              </div>
              <PrimaryButton disabled={windowSaving} onClick={saveAllWithdrawalSettings} style={{ width: isMobile ? "100%" : 220 }}>
                {windowSaving ? "Saving Settings..." : "💾 Save All Withdrawal Rules"}
              </PrimaryButton>
            </div>
          </div>
        )}
      </div>

      {/* Filters */}
      <div
        style={{
          border: "1px solid #e2e8f0",
          borderRadius: 12,
          padding: 12,
          background: "#fff",
          marginBottom: 12,
          display: !isMobile || showFilters ? "block" : "none",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isDesktop
              ? "repeat(4, minmax(0, 1fr))"
              : "repeat(2, minmax(0, 1fr))",
            gap: 12,
          }}
        >
          <Select
            label="Status"
            value={filters.status}
            onChange={(v) => setF("status", v)}
            options={statusOptions}
            style={{ width: "100%" }}
          />
          <TextInput
            label="User"
            value={filters.user}
            onChange={(v) => setF("user", v)}
            placeholder="user id / username / name / phone"
            style={{ width: "100%" }}
          />
          <TextInput
            label="From Date"
            type="date"
            value={filters.date_from}
            onChange={(v) => setF("date_from", v)}
            placeholder=""
            style={{ width: "100%" }}
          />
          <TextInput
            label="To Date"
            type="date"
            value={filters.date_to}
            onChange={(v) => setF("date_to", v)}
            placeholder=""
            style={{ width: "100%" }}
          />
          <TextInput
            label="Min Amount"
            value={filters.min_amount}
            onChange={(v) => setF("min_amount", v)}
            placeholder="e.g. 100"
            style={{ width: "100%" }}
          />
          <TextInput
            label="Max Amount"
            value={filters.max_amount}
            onChange={(v) => setF("max_amount", v)}
            placeholder="e.g. 5000"
            style={{ width: "100%" }}
          />
          <Select
            label="Method"
            value={filters.method}
            onChange={(v) => setF("method", v)}
            options={methodOptions}
            style={{ width: "100%" }}
          />
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: 8,
            marginTop: 12,
            alignItems: isMobile ? "stretch" : "center",
          }}
        >
          <PrimaryButton
            onClick={fetchWithdrawals}
            disabled={loading}
            style={{ width: isMobile ? "100%" : "auto" }}
          >
            {loading ? "Loading..." : "Apply Filters"}
          </PrimaryButton>
          <SecondaryButton
            onClick={resetFilters}
            disabled={loading}
            style={{ width: isMobile ? "100%" : "auto" }}
          >
            Reset
          </SecondaryButton>
          {err ? (
            <div style={{ color: "#dc2626", fontSize: 13, marginLeft: isMobile ? 0 : "auto" }}>
              {err}
            </div>
          ) : null}
        </div>
      </div>

      {/* Results */}
      {isMobile ? (
        // Mobile: Cards (avoid heavy table rendering)
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {rows.map((r) => {
            const statusBadge = getStatusBadge(r);
            const trieduInfo = parseTrieduClaimNote(r.note, directBankFeePercent);
            const isTrieduClaim = Boolean(trieduInfo?.isClaim);
            const displayGross = (isTrieduClaim && trieduInfo?.gross != null) ? trieduInfo.gross : Number(r.amount || 0);
            const gross = Number(r.amount || 0);
            const taxAmt = isTrieduClaim ? (trieduInfo?.feeAmount || 0) : q2(gross * Number(taxLabel || 0) / 100);
            const net = isTrieduClaim ? gross : q2(gross - taxAmt);
            return (
              <div
                key={r.id}
                style={{
                  border: "1px solid #e2e8f0",
                  borderRadius: 12,
                  background: "#fff",
                  padding: 12,
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <div style={{ fontSize: 14, fontWeight: 800, color: "#0f172a" }}>
                        {r.full_name || r.username || "User"}
                      </div>
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 800,
                          padding: "2px 8px",
                          borderRadius: 999,
                          background: (r.rank_level > 0) ? "#e0e7ff" : "#f1f5f9",
                          color: (r.rank_level > 0) ? "#3730a3" : "#475569",
                          border: `1px solid ${(r.rank_level > 0) ? "#c7d2fe" : "#e2e8f0"}`,
                        }}
                      >
                        {r.rank_label || r.rank_name || (r.rank_level ? `Layer ${r.rank_level}` : "Free Tier (L0)")}
                      </span>
                      {r.rank_upgrades_count > 0 && (
                        <span style={{ fontSize: 11, color: "#6366f1", fontWeight: 700 }}>
                          ⚡ {r.rank_upgrades_count} upgrade{r.rank_upgrades_count > 1 ? "s" : ""}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: "#64748b", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {r.username ? `@${r.username}` : ""}
                    </div>
                  </div>
                  <div style={{ flexShrink: 0 }}>{statusBadge}</div>
                </div>

                {/* Purchased Packages (Mobile) */}
                <div style={{ background: "#f8fafc", padding: "8px 10px", borderRadius: 8, border: "1px solid #e2e8f0" }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", marginBottom: 4 }}>
                    Purchased Packages
                  </div>
                  {Array.isArray(r.package_badges) && r.package_badges.length > 0 ? (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                      {r.package_badges.map((b, idx) => (
                        <span
                          key={idx}
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            padding: "2px 6px",
                            borderRadius: 4,
                            background: b.toLowerCase().includes("spp") ? "#ecfdf5" : "#eff6ff",
                            color: b.toLowerCase().includes("spp") ? "#065f46" : "#1e40af",
                            border: `1px solid ${b.toLowerCase().includes("spp") ? "#a7f3d0" : "#bfdbfe"}`,
                          }}
                        >
                          📦 {b}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span style={{ fontSize: 11, color: "#94a3b8", fontStyle: "italic" }}>No packages purchased</span>
                  )}
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 8 }}>
                  <div>
                    <div style={{ fontSize: 11, color: "#64748b" }}>{isTrieduClaim ? "Gross Requested" : "Amount"}</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>
                      ₹{Number(displayGross || 0).toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: "#64748b" }}>{isTrieduClaim ? "Final Payout" : "After Tax"}</div>
                    <div style={{ fontSize: 14, fontWeight: 800, color: "#0f172a" }}>
                      ₹{Number(net || 0).toFixed(2)}
                    </div>
                    <div style={{ fontSize: 11, color: isTrieduClaim ? "#059669" : "#64748b", fontWeight: isTrieduClaim ? 700 : 400 }}>
                      {isTrieduClaim
                        ? (trieduInfo?.feePercent > 0
                            ? `E-Edu Net (${trieduInfo.feePercent}% fee deducted = ₹${trieduInfo.feeAmount.toFixed(2)})`
                            : "E-Edu Net (0% fee - 100% Payout)")
                        : `Tax ${Number(taxLabel).toFixed(2)}% = ₹${Number(taxAmt || 0).toFixed(2)}`}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: "#64748b" }}>Method</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>
                      {r.method?.toUpperCase?.() || ""}
                    </div>
                  </div>
                  <div style={{ gridColumn: "1 / -1" }}>
                    <div style={{ fontSize: 11, color: "#64748b" }}>Requested</div>
                    <div style={{ fontSize: 13, color: "#0f172a" }}>{formatDateTime(r.requested_at)}</div>
                  </div>

                  {/* Extra details (if present in API response) */}
                  {r?.phone ? (
                    <div style={{ gridColumn: "1 / -1" }}>
                      <div style={{ fontSize: 11, color: "#64748b" }}>Phone</div>
                      <div style={{ fontSize: 13, color: "#0f172a" }}>{r.phone}</div>
                    </div>
                  ) : null}
                  {r?.pincode ? (
                    <div style={{ gridColumn: "1 / -1" }}>
                      <div style={{ fontSize: 11, color: "#64748b" }}>Pincode</div>
                      <div style={{ fontSize: 13, color: "#0f172a" }}>{r.pincode}</div>
                    </div>
                  ) : null}
                  {r?.bank_account_number || r?.account_number ? (
                    <div style={{ gridColumn: "1 / -1" }}>
                      <div style={{ fontSize: 11, color: "#64748b" }}>Bank Account</div>
                      <div style={{ fontSize: 13, color: "#0f172a" }}>{r.bank_account_number || r.account_number}</div>
                    </div>
                  ) : null}
                  {r?.ifsc_code || r?.ifsc ? (
                    <div style={{ gridColumn: "1 / -1" }}>
                      <div style={{ fontSize: 11, color: "#64748b" }}>IFSC</div>
                      <div style={{ fontSize: 13, color: "#0f172a" }}>{r.ifsc_code || r.ifsc}</div>
                    </div>
                  ) : null}
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {r.status === "pending" ? (
                    <>
                      <ActionButton onClick={() => handleApprove(r)} variant="approve">
                        Approve
                      </ActionButton>
                      <ActionButton onClick={() => handleReject(r)} variant="reject">
                        Reject
                      </ActionButton>
                    </>
                  ) : (
                    <div style={{ color: "#64748b", fontSize: 12 }}>No actions</div>
                  )}
                </div>
              </div>
            );
          })}
          {!loading && rows.length === 0 ? <div style={{ padding: 12, color: "#64748b" }}>No results</div> : null}
        </div>
      ) : (
        // Tablet/Desktop: Table
        <div
          style={{
            border: "1px solid #e2e8f0",
            borderRadius: 12,
            overflow: "hidden",
            background: "#fff",
            width: "100%",
          }}
        >
          <div
            style={{
              overflowX: "auto",
              WebkitOverflowScrolling: "touch",
            }}
          >
            <div
              style={{
                minWidth: 1580,
              }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "40px 55px 120px 130px 145px 180px 95px 110px 95px 85px 145px 80px 95px 95px 130px 160px",
                  gap: 8,
                  padding: 12,
                  background: "#f8fafc",
                  borderBottom: "1px solid #e2e8f0",
                  fontWeight: 800,
                  color: "#0f172a",
                  fontSize: 13,
                  alignItems: "center",
                }}
              >
                <div>
                  <input
                    type="checkbox"
                    checked={rows.length > 0 && selectedIds.length === rows.length}
                    onChange={(e) => {
                      if (e.target.checked) setSelectedIds(rows.map((r) => r.id));
                      else setSelectedIds([]);
                    }}
                    style={{ cursor: "pointer", width: 16, height: 16 }}
                  />
                </div>
                <div>ID</div>
                <div>User</div>
                <div>Name</div>
                <div>Rank & Layer</div>
                <div>Purchased Packages</div>
                <div>Solvency</div>
                <div>Phone</div>
                <div>Amount</div>
                <div>Method</div>
                <div>Bank / IFSC</div>
                <div>Tax %</div>
                <div>Final</div>
                <div>Status</div>
                <div>Requested</div>
                <div>Actions</div>
              </div>

              <div>
                {rows.map((r) => {
                  const statusBadge = getStatusBadge(r);
                  const trieduInfo = parseTrieduClaimNote(r.note, directBankFeePercent);
                  const isTrieduClaim = Boolean(trieduInfo?.isClaim);
                  const displayGross = (isTrieduClaim && trieduInfo?.gross != null) ? trieduInfo.gross : Number(r.amount || 0);
                  const gross = Number(r.amount || 0);
                  const taxAmt = isTrieduClaim ? (trieduInfo?.feeAmount || 0) : q2(gross * Number(taxLabel || 0) / 100);
                  const net = isTrieduClaim ? gross : q2(gross - taxAmt);
                  const isChecked = selectedIds.includes(r.id);
                  const isSolvent = Number(r.user_wallet_balance || gross) >= gross;
                  return (
                    <div
                      key={r.id}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "40px 55px 120px 130px 145px 180px 95px 110px 95px 85px 145px 80px 95px 95px 130px 160px",
                        gap: 8,
                        padding: 12,
                        borderBottom: "1px solid #e2e8f0",
                        alignItems: "center",
                        fontSize: 13,
                        backgroundColor: isChecked ? "#f0f9ff" : "#fff",
                      }}
                    >
                      <div>
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => {
                            if (e.target.checked) setSelectedIds((s) => [...s, r.id]);
                            else setSelectedIds((s) => s.filter((id) => id !== r.id));
                          }}
                          style={{ cursor: "pointer", width: 16, height: 16 }}
                        />
                      </div>
                      <div style={{ color: "#0f172a", fontWeight: 700 }}>{r.id}</div>
                      <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{r.username}</div>
                      <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{r.full_name || ""}</div>
                      
                      {/* Rank & Upgrade Layer */}
                      <div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                          <span
                            style={{
                              fontSize: 11.5,
                              fontWeight: 800,
                              padding: "2px 8px",
                              borderRadius: 999,
                              background: (r.rank_level > 0) ? "#e0e7ff" : "#f1f5f9",
                              color: (r.rank_level > 0) ? "#3730a3" : "#475569",
                              border: `1px solid ${(r.rank_level > 0) ? "#c7d2fe" : "#e2e8f0"}`,
                              whiteSpace: "nowrap",
                              display: "inline-block",
                              width: "fit-content",
                            }}
                          >
                            {r.rank_label || r.rank_name || (r.rank_level ? `Layer ${r.rank_level}` : "Free Tier (L0)")}
                          </span>
                          {r.rank_upgrades_count > 0 && (
                            <span style={{ fontSize: 10.5, color: "#6366f1", fontWeight: 700 }}>
                              ⚡ {r.rank_upgrades_count} upgrade{r.rank_upgrades_count > 1 ? "s" : ""}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Purchased Packages */}
                      <div>
                        {Array.isArray(r.package_badges) && r.package_badges.length > 0 ? (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                            {r.package_badges.map((b, idx) => (
                              <span
                                key={idx}
                                title={b}
                                style={{
                                  fontSize: 10.5,
                                  fontWeight: 700,
                                  padding: "2px 6px",
                                  borderRadius: 4,
                                  background: b.toLowerCase().includes("spp")
                                    ? "#ecfdf5"
                                    : b.toLowerCase().includes("tour")
                                    ? "#fffbeb"
                                    : "#f8fafc",
                                  color: b.toLowerCase().includes("spp")
                                    ? "#065f46"
                                    : b.toLowerCase().includes("tour")
                                    ? "#92400e"
                                    : "#334155",
                                  border: `1px solid ${
                                    b.toLowerCase().includes("spp")
                                      ? "#a7f3d0"
                                      : b.toLowerCase().includes("tour")
                                      ? "#fde68a"
                                      : "#e2e8f0"
                                  }`,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                📦 {b}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span style={{ fontSize: 11, color: "#94a3b8", fontStyle: "italic" }}>No packages</span>
                        )}
                      </div>

                      <div>
                        <span
                          title={isSolvent ? "Verified against wallet balance" : "Risk: check wallet audit"}
                          style={{
                            fontSize: 11,
                            fontWeight: 800,
                            padding: "2px 8px",
                            borderRadius: 6,
                            background: isSolvent ? "#dcfce7" : "#fee2e2",
                            color: isSolvent ? "#15803d" : "#991b1b",
                            border: `1px solid ${isSolvent ? "#86efac" : "#fca5a5"}`,
                          }}
                        >
                          {isSolvent ? "🟢 Clean" : "🔴 Review"}
                        </span>
                      </div>
                      <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{r.phone || ""}</div>
                      <div style={{ fontWeight: 700 }}>₹{Number(displayGross || 0).toFixed(2)}</div>
                      <div>{r.method?.toUpperCase?.() || ""}</div>
                      <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                        {(r.bank_account_number || r.account_number) ? (
                          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            <span style={{ fontWeight: 700 }}>{r.bank_account_number || r.account_number}</span>
                            <span style={{ color: "#64748b", fontSize: 12 }}>{r.ifsc_code || r.ifsc || ""}</span>
                          </div>
                        ) : (
                          <span style={{ color: "#94a3b8" }}>—</span>
                        )}
                      </div>
                      <div>{isTrieduClaim ? (trieduInfo?.feePercent > 0 ? `${trieduInfo.feePercent}% (Net)` : "0% (Free)") : `${Number(taxLabel).toFixed(2)}%`}</div>
                      <div style={{ fontWeight: 800 }}>₹{Number(net || 0).toFixed(2)}</div>
                      <div>{statusBadge}</div>
                      <div style={{ color: "#334155", fontSize: 12 }}>{formatDateTime(r.requested_at)}</div>
                      <div
                        style={{
                          display: "flex",
                          gap: 8,
                          flexWrap: "wrap",
                          justifyContent: "flex-start",
                        }}
                      >
                        {r.status === "pending" ? (
                          <>
                            <ActionButton onClick={() => handleApprove(r)} variant="approve">
                              Approve
                            </ActionButton>
                            <ActionButton onClick={() => handleReject(r)} variant="reject">
                              Reject
                            </ActionButton>
                          </>
                        ) : (
                          <span style={{ color: "#64748b", fontSize: 12 }}>No actions</span>
                        )}
                      </div>
                    </div>
                  );
                })}
                {!loading && rows.length === 0 ? (
                  <div style={{ padding: 12, color: "#64748b" }}>No results</div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}





